import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DateTimePicker from "src/components/DateTimePickerRange";
import { IPhases } from "../Project/Components/Phases/interface";
import { IRefSprintModals } from "./interface";
import addAndChangeSchemaSprint from "./schema";
import { IProject, IProjectModals } from "../Project/interface";
import { IListProjectLevelData } from "../Panel/Components/ListProjectsLevel/interface";

const ModalSprint: React.ForwardRefRenderFunction<IRefSprintModals, IProjectModals> = ({
	onCallbackProgram
}, ref) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	
	const [projectSelected, setProjectSelected] = useState<IListProjectLevelData>();
	const [phaseSelected, setPhaseSelected] = useState<IPhases>();

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control: controlAddOrChange, 
		reset : resetAddOrChange,
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange 
	} = useForm();

	const submitAddOrChange = async (dataForm: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await addAndChangeSchemaSprint(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "ProjectPhase/Insert",
					body: {
						...dataForm,
						pra003Id: 0,
						pra003Pra002Id: projectSelected?.id || 0,
						pra003Ordem: 2,
						pra003Vlrestimado: 0,
						pra003Vlrestreceita: 0,
						pra003Peso: 1,
						pra003Situacao: 1	
					}
				});

				onCallbackProgram(projectSelected?.parentId || 0);

			}else{
				await RCCServicesApi.putRequest({
					method: "ProjectPhase/Update",
					body: {
						...phaseSelected,
						pra003Titulo: dataForm.pra003Titulo,
						pra003Dtiniest: dataForm.pra003Dtiniest,
						pra003Dtfimest: dataForm.pra003Dtfimest
					}
				});

				onCallbackProgram(projectSelected?.parentId || 0);
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Sprint adicionada com sucesso") : translate("Sprint alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar sprint") : translate("Erro ao alterar sprint"), addToast);
		}
	};

	const actionButtonAdd = (project: IProject) => {
		setPhaseSelected(undefined);
		setProjectSelected({
			id: project.pra002Id,
			parentId: project.pra002Pra001Id,
			level: 1,
			pra002Modelo: project.pra002Modelo,
			users: [{
				name: project.pra002Efa005Nome,
				image: project.pra002Efa005Imagem
			}],
			name: project.pra002Titulo,
			startDate: project.pra002Dtiniest,
			endDate: project.pra002Dtfimest,
			perPrevision: project.expectedPercentage,
			perConclusion: project.completionPercentage,
			perConclusionColor: project.completionPercentageColor || "purple",
			situation: project.situation,
			count: project.count,
			completed: project.completed,
			permission: project.permission
		});
		resetAddOrChange(); 
		setIsAdd(true);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (phaseId: number, project: IProject) => {
		setProjectSelected({
			id: project.pra002Id,
			parentId: project.pra002Pra001Id,
			level: 1,
			pra002Modelo: project.pra002Modelo,
			users: [{
				name: project.pra002Efa005Nome,
				image: project.pra002Efa005Imagem
			}],
			name: project.pra002Titulo,
			startDate: project.pra002Dtiniest,
			endDate: project.pra002Dtfimest,
			perPrevision: project.expectedPercentage,
			perConclusion: project.completionPercentage,
			perConclusionColor: project.completionPercentageColor || "purple",
			situation: project.situation,
			count: project.count,
			completed: project.completed,
			permission: project.permission
		});
		resetAddOrChange(); 
		setIsAdd(false);
		getPhase(phaseId);
	};

	async function getPhase(id: number){
		const result = await RCCServicesApi.getRequest({
			method: "ProjectPhase/Select",
			urlParameters: {id}
		});
		setPhaseSelected(result.data);
		modalAddOrChange.current?.openModal();
	}

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));

	return(
		<>
			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Sprint") : translate("Alterar Sprint"),
					subtitle: isAdd ? `${translate("Projeto")}: ${projectSelected?.name}` : `${translate("Sprint")}: ${projectSelected?.name}`,
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"

				>
					<RCCGrid
						container
						item
						alignItems="flex-end"
					>
						<input 
							type="text"
							value={phaseSelected?.pra003Id}
							{...registerAddOrChange("pra003Id")} 
							style={{ display: "none" }} 
							readOnly
						/>
						<RCCGrid
							container
							item
							justifyContent="center"
							xs={12}
							sm={12}
						>
							<RCCGrid
								xs={12}
							>
								<RCCInput 
									label={translate("Título")}
									register={registerAddOrChange}
									name="pra003Titulo"
									maxLength={200}
									defaultValue={phaseSelected?.pra003Titulo}
									error={errorsAddOrChange.pra003Titulo}
									required
								/>
							</RCCGrid>
							<RCCGrid
								xs={12}
							>
								<DateTimePicker
									control={controlAddOrChange}
									type="datePicker"
									label={translate("Data Estimada")}
									labelSeparator={translate("Até")}
									nameInit="pra003Dtiniest"
									nameEnd="pra003Dtfimest"
									defaultValueInit={phaseSelected?.pra003Dtiniest ? phaseSelected?.pra003Dtiniest : ""}
									defaultValueEnd={phaseSelected?.pra003Dtfimest ? phaseSelected?.pra003Dtfimest : ""}
									error={errorsAddOrChange.pra003Dtiniest || errorsAddOrChange.pra003Dtfimest}
									required
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
		
	);
};

export default forwardRef(ModalSprint);
